import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress as ProgressBar } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowLeft, Pencil, Trash2, Plus, GraduationCap, BookOpen, ListChecks, Users, Clock,
  UserPlus, Video, FileText, CheckSquare, Layers,
} from "lucide-react";
import { toast } from "sonner";

const AUDIENCES = ["trainer", "location_manager", "franchise_owner", "head_office"];
const STATUSES = ["draft", "active", "archived"];
const LESSON_TYPES = ["text", "video", "checklist", "quiz"];
const TYPE_ICON = { text: FileText, video: Video, checklist: CheckSquare, quiz: ListChecks };
const field = (o, keys) => Object.fromEntries(keys.map((k) => [k, o?.[k] ?? ""]));

export default function AcademyCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRefData();
  const canManage = ["super_admin", "head_office_admin"].includes(user?.role);
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [courseDlg, setCourseDlg] = useState(false);
  const [modDlg, setModDlg] = useState(null);       // {editing}
  const [lessonDlg, setLessonDlg] = useState(null); // {moduleId, editing}
  const [chkDlg, setChkDlg] = useState(null);       // {editing}
  const [assignDlg, setAssignDlg] = useState(false);
  const [form, setForm] = useState({});
  const [assign, setAssign] = useState({ target_type: "location_trainers", location_id: "", trainer_id: "", user_id: "" });

  const load = useCallback(async () => {
    try { const r = await api.get(`/academy/courses/${id}/detail`); setC(r.data); }
    catch { setErr(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-center py-20 text-muted-foreground">Course not found.</div>;
  if (!c) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const lessonsCount = c.modules.reduce((a, m) => a + m.lessons.length, 0);

  const openCourse = () => { setForm({ ...field(c, ["name", "audience", "certification_type", "estimated_duration", "description", "status", "valid_for_months"]), required: c.required }); setCourseDlg(true); };
  const saveCourse = async () => { try { await api.put(`/academy/courses/${id}`, { ...form, valid_for_months: form.valid_for_months ? Number(form.valid_for_months) : null }); toast.success("Course updated"); setCourseDlg(false); await load(); } catch { toast.error("Failed"); } };

  const openMod = (m) => { setForm(m ? field(m, ["title", "description", "sort_order"]) : { title: "", description: "", sort_order: c.modules.length }); setModDlg({ editing: m }); };
  const saveMod = async () => {
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
      if (modDlg.editing) await api.put(`/academy/modules/${modDlg.editing.id}`, payload);
      else await api.post(`/academy/courses/${id}/modules`, payload);
      toast.success("Module saved"); setModDlg(null); await load();
    } catch { toast.error("Failed"); }
  };
  const delMod = async (m) => { if (!window.confirm(`Delete module "${m.title}"? Its lessons will be removed.`)) return; await api.delete(`/academy/modules/${m.id}`); toast.success("Deleted"); await load(); };

  const openLesson = (moduleId, l, count) => { setForm(l ? field(l, ["title", "lesson_type", "content", "video_url", "sort_order", "required"]) : { title: "", lesson_type: "text", content: "", video_url: "", sort_order: count, required: true }); setLessonDlg({ moduleId, editing: l }); };
  const saveLesson = async () => {
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 0, required: form.required !== false };
      if (lessonDlg.editing) await api.put(`/academy/lessons/${lessonDlg.editing.id}`, payload);
      else await api.post(`/academy/modules/${lessonDlg.moduleId}/lessons`, payload);
      toast.success("Lesson saved"); setLessonDlg(null); await load();
    } catch { toast.error("Failed"); }
  };
  const delLesson = async (l) => { if (!window.confirm(`Delete lesson "${l.title}"?`)) return; await api.delete(`/academy/lessons/${l.id}`); toast.success("Deleted"); await load(); };

  const openChk = (x) => { setForm(x ? field(x, ["title", "description"]) : { title: "", description: "", required: true }); if (x) setForm((f) => ({ ...f, required: x.required })); setChkDlg({ editing: x }); };
  const saveChk = async () => {
    try {
      const payload = { ...form, required: form.required !== false };
      if (chkDlg.editing) await api.put(`/academy/checklist/${chkDlg.editing.id}`, payload);
      else await api.post(`/academy/courses/${id}/checklist`, payload);
      toast.success("Checklist item saved"); setChkDlg(null); await load();
    } catch { toast.error("Failed"); }
  };
  const delChk = async (x) => { if (!window.confirm(`Delete "${x.title}"?`)) return; await api.delete(`/academy/checklist/${x.id}`); toast.success("Deleted"); await load(); };

  const doAssign = async () => {
    try {
      const payload = { course_id: id, target_type: assign.target_type };
      if (assign.target_type === "location_trainers") payload.location_id = assign.location_id;
      if (assign.target_type === "trainer") payload.trainer_id = assign.trainer_id;
      if (assign.target_type === "user") payload.user_id = assign.user_id;
      const r = await api.post("/academy/assign", payload);
      toast.success(`Assigned to ${r.data.assigned} ${r.data.assigned === 1 ? "person" : "people"}`); setAssignDlg(false); await load();
    } catch { toast.error("Assign failed"); }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/academy")} data-testid="course-back"><ArrowLeft size={16} /> Back to HV Academy</Button>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="h-14 w-14 rounded-2xl bg-neutral-900 flex items-center justify-center shrink-0"><GraduationCap size={28} className="text-primary-foreground" strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{c.name}</h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <StatusBadge value={c.status} />
              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground capitalize">{String(c.audience).replace(/_/g, " ")}</span>
              {c.required && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Required</span>}
              {c.estimated_duration && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> {c.estimated_duration}</span>}
              {c.certification_type && <span className="text-xs text-muted-foreground">{c.certification_type}</span>}
            </div>
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={openCourse} data-testid="course-edit-btn"><Pencil size={15} /> Edit</Button>
            <Button className="gap-2" onClick={() => setAssignDlg(true)} data-testid="course-assign-btn"><UserPlus size={15} /> Assign</Button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {c.description && <Card className="border-border"><CardContent className="p-5"><p className="text-sm text-muted-foreground">{c.description}</p></CardContent></Card>}

          {/* Modules */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Layers size={18} strokeWidth={1.5} /> Modules ({c.modules.length}) · {lessonsCount} lessons</h2>
              {canManage && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openMod(null)} data-testid="module-add-btn"><Plus size={14} /> Add module</Button>}
            </div>
            <Accordion type="multiple" className="space-y-2">
              {c.modules.map((m, i) => (
                <AccordionItem key={m.id} value={m.id} className="border border-border rounded-lg px-4 bg-card" data-testid="module-item">
                  <AccordionTrigger className="hover:no-underline py-3">
                    <div className="flex items-center gap-3 text-left flex-1">
                      <span className="h-6 w-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                      <div className="flex-1"><div className="font-medium text-sm">{m.title}</div><div className="text-xs text-muted-foreground">{m.lessons.length} lesson{m.lessons.length === 1 ? "" : "s"}</div></div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 space-y-3">
                    {m.description && <p className="text-sm text-muted-foreground">{m.description}</p>}
                    <div className="space-y-2">
                      {m.lessons.map((l) => {
                        const Icon = TYPE_ICON[l.lesson_type] || FileText;
                        return (
                          <div key={l.id} className="rounded-lg border border-border/60 p-3" data-testid="lesson-item">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 flex-1">
                                <Icon size={15} className="text-primary mt-0.5" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2"><span className="text-sm font-medium">{l.title}</span><span className="text-[10px] text-muted-foreground capitalize">{l.lesson_type}</span>{l.required === false && <span className="text-[10px] text-muted-foreground">optional</span>}</div>
                                  {l.content && <p className="text-xs text-muted-foreground mt-1">{l.content}</p>}
                                  {l.video_url && <p className="text-xs text-primary mt-1 truncate">{l.video_url}</p>}
                                </div>
                              </div>
                              {canManage && (
                                <div className="flex gap-0.5">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openLesson(m.id, l, m.lessons.length)} data-testid="lesson-edit-btn"><Pencil size={13} /></Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => delLesson(l)} data-testid="lesson-delete-btn"><Trash2 size={13} className="text-destructive" /></Button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {m.lessons.length === 0 && <p className="text-xs text-muted-foreground">No lessons yet.</p>}
                    </div>
                    {canManage && (
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => openLesson(m.id, null, m.lessons.length)} data-testid="lesson-add-btn"><Plus size={12} /> Add lesson</Button>
                        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => openMod(m)}><Pencil size={12} /> Edit module</Button>
                        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-destructive" onClick={() => delMod(m)}><Trash2 size={12} /> Delete</Button>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
              {c.modules.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No modules yet.</p>}
            </Accordion>
          </div>

          {/* Checklist */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><ListChecks size={18} strokeWidth={1.5} /> Certification Checklist ({c.checklist.length})</h2>
              {canManage && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openChk(null)} data-testid="checklist-add-btn"><Plus size={14} /> Add item</Button>}
            </div>
            <Card className="border-border"><CardContent className="p-4 space-y-2">
              {c.checklist.map((x) => (
                <div key={x.id} className="flex items-start justify-between gap-2 rounded-lg border border-border/60 p-3" data-testid="checklist-item">
                  <div className="flex items-start gap-2"><CheckSquare size={15} className="text-primary mt-0.5" /><div><div className="text-sm font-medium">{x.title}</div>{x.description && <div className="text-xs text-muted-foreground">{x.description}</div>}</div></div>
                  {canManage && <div className="flex gap-0.5"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openChk(x)} data-testid="checklist-edit-btn"><Pencil size={13} /></Button><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => delChk(x)} data-testid="checklist-delete-btn"><Trash2 size={13} className="text-destructive" /></Button></div>}
                </div>
              ))}
              {c.checklist.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No checklist items yet.</p>}
            </CardContent></Card>
          </div>
        </div>

        {/* Sidebar: enrolled team */}
        <div className="space-y-6">
          <Card className="border-border"><CardContent className="p-5 space-y-2">
            <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground flex items-center gap-1.5"><BookOpen size={14} /> Lessons</span><span className="font-medium">{lessonsCount}</span></div>
            <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground flex items-center gap-1.5"><ListChecks size={14} /> Checklist items</span><span className="font-medium">{c.checklist.length}</span></div>
            <div className="flex items-center justify-between text-sm"><span className="text-muted-foreground flex items-center gap-1.5"><Users size={14} /> Enrolled</span><span className="font-medium">{c.enrollments.length}</span></div>
            <div className="text-xs text-muted-foreground pt-1">Created by {c.created_by || "—"} · Updated {fmtDate(c.updated_at)}</div>
          </CardContent></Card>

          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-base flex items-center gap-2"><Users size={16} strokeWidth={1.5} /> Enrolled</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {c.enrollments.map((e) => (
                <div key={e.id} className="p-2.5 rounded-lg border border-border/60" data-testid="course-enrollment-row">
                  <div className="flex items-center justify-between"><span className="text-sm font-medium">{e.trainer_name}</span><StatusBadge value={e.certification_status} /></div>
                  <div className="mt-1.5 flex items-center gap-2"><ProgressBar value={e.progress_percentage} className="h-1.5 flex-1" /><span className="text-xs text-muted-foreground">{e.progress_percentage}%</span></div>
                  <div className="text-[11px] text-muted-foreground mt-1">{e.location_name}</div>
                </div>
              ))}
              {c.enrollments.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No one enrolled yet.</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Course edit */}
      <Dialog open={courseDlg} onOpenChange={setCourseDlg}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Course</DialogTitle><DialogDescription>Update course details and status.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="course-edit-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <Sel label="Audience" value={form.audience} onChange={(v) => setForm({ ...form, audience: v })} options={AUDIENCES} />
              <div className="grid gap-1.5"><Label>Certification type</Label><Input value={form.certification_type || ""} onChange={(e) => setForm({ ...form, certification_type: e.target.value })} /></div>
            </div>
            <div className="grid gap-1.5"><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-1.5"><Label>Duration</Label><Input value={form.estimated_duration || ""} onChange={(e) => setForm({ ...form, estimated_duration: e.target.value })} /></div>
              <Sel label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={STATUSES} testid="course-edit-status" />
              <div className="grid gap-1.5"><Label>Valid (mo)</Label><Input type="number" value={form.valid_for_months || ""} onChange={(e) => setForm({ ...form, valid_for_months: e.target.value })} /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-3"><Label>Required</Label><Switch checked={!!form.required} onCheckedChange={(v) => setForm({ ...form, required: v })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCourseDlg(false)}>Cancel</Button><Button onClick={saveCourse} data-testid="course-edit-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Module dialog */}
      <Dialog open={!!modDlg} onOpenChange={(o) => !o && setModDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{modDlg?.editing ? "Edit Module" : "New Module"}</DialogTitle><DialogDescription>A module groups related lessons.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-1.5 col-span-2"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="module-title" /></div>
              <div className="grid gap-1.5"><Label>Order</Label><Input type="number" value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></div>
            </div>
            <div className="grid gap-1.5"><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setModDlg(null)}>Cancel</Button><Button onClick={saveMod} disabled={!form.title} data-testid="module-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson dialog */}
      <Dialog open={!!lessonDlg} onOpenChange={(o) => !o && setLessonDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{lessonDlg?.editing ? "Edit Lesson" : "New Lesson"}</DialogTitle><DialogDescription>Define the lesson content and type.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-1.5 col-span-2"><Label>Title</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="lesson-title" /></div>
              <Sel label="Type" value={form.lesson_type} onChange={(v) => setForm({ ...form, lesson_type: v })} options={LESSON_TYPES} />
            </div>
            <div className="grid gap-1.5"><Label>Content</Label><Textarea value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-1.5 col-span-2"><Label>Video / link (placeholder)</Label><Input value={form.video_url || ""} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="Optional" /></div>
              <div className="grid gap-1.5"><Label>Order</Label><Input type="number" value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-3"><Label>Required to certify</Label><Switch checked={form.required !== false} onCheckedChange={(v) => setForm({ ...form, required: v })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setLessonDlg(null)}>Cancel</Button><Button onClick={saveLesson} disabled={!form.title} data-testid="lesson-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checklist dialog */}
      <Dialog open={!!chkDlg} onOpenChange={(o) => !o && setChkDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{chkDlg?.editing ? "Edit Checklist Item" : "New Checklist Item"}</DialogTitle><DialogDescription>A competency the learner must demonstrate.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label>Item</Label><Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="checklist-title" /></div>
            <div className="grid gap-1.5"><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="flex items-center justify-between rounded-lg border border-border/60 p-3"><Label>Required to certify</Label><Switch checked={form.required !== false} onCheckedChange={(v) => setForm({ ...form, required: v })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setChkDlg(null)}>Cancel</Button><Button onClick={saveChk} disabled={!form.title} data-testid="checklist-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignDlg} onOpenChange={setAssignDlg}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Assign Course</DialogTitle><DialogDescription>Enrol staff in {c.name}.</DialogDescription></DialogHeader>
          <div className="grid gap-4 py-2">
            <Sel label="Assign to" value={assign.target_type} onChange={(v) => setAssign({ ...assign, target_type: v })}
              options={[{ v: "location_trainers", l: "All trainers at a location" }, { v: "trainer", l: "Individual trainer" }]}
              testid="assign-target" />
            {assign.target_type === "location_trainers" && (
              <div className="grid gap-1.5"><Label>Location</Label>
                <Select value={assign.location_id} onValueChange={(v) => setAssign({ ...assign, location_id: v })}><SelectTrigger data-testid="assign-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>{(ref?.locations || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
            {assign.target_type === "trainer" && (
              <div className="grid gap-1.5"><Label>Trainer</Label>
                <Select value={assign.trainer_id} onValueChange={(v) => setAssign({ ...assign, trainer_id: v })}><SelectTrigger data-testid="assign-trainer"><SelectValue placeholder="Select trainer" /></SelectTrigger>
                  <SelectContent>{(ref?.trainers || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAssignDlg(false)}>Cancel</Button>
            <Button onClick={doAssign} disabled={(assign.target_type === "location_trainers" && !assign.location_id) || (assign.target_type === "trainer" && !assign.trainer_id)} data-testid="assign-save">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Sel({ label, value, onChange, options, testid }) {
  const opts = options.map((o) => (typeof o === "string" ? { v: o, l: o } : o));
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="capitalize"><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.v} value={o.v} className="capitalize">{String(o.l).replace(/_/g, " ")}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
